import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Link2, Plus, Copy, Trash2, ToggleLeft, ToggleRight, ExternalLink, X, Eye, EyeOff, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

const BASE_URL = window.location.origin;

export default function ClientPortal() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createForm, setCreateForm] = useState({
    email: '', expiresInDays: 90,
    permissions: { view_progress: true, view_photos: true, view_milestones: true, view_documents: false, view_checklists: false },
  });

  useEffect(() => {
    Promise.all([
      api.get('/projects').then(r => setProjects(r.data.data || r.data || [])),
      api.get('/client-portal/tokens').then(r => setTokens(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const createToken = async () => {
    if (!selectedProject) return toast.error('Selecciona un proyecto');
    if (!createForm.email) return toast.error('Email del cliente requerido');
    try {
      const { data } = await api.post('/client-portal/tokens', {
        projectId: selectedProject,
        email: createForm.email,
        permissions: createForm.permissions,
        expiresInDays: createForm.expiresInDays || null,
      });
      setTokens(prev => [data, ...prev]);
      setShowCreate(false);
      setCreateForm({ email: '', expiresInDays: 90, permissions: { view_progress: true, view_photos: true, view_milestones: true, view_documents: false, view_checklists: false } });
      toast.success('Acceso creado');
    } catch (e) { toast.error('Error'); }
  };

  const toggleToken = async (id, isActive) => {
    try {
      await api.put(`/client-portal/tokens/${id}`, { isActive: !isActive });
      setTokens(prev => prev.map(t => t.id === id ? { ...t, is_active: !isActive } : t));
    } catch (e) { toast.error('Error'); }
  };

  const deleteToken = async (id) => {
    if (!window.confirm('¿Eliminar este acceso?')) return;
    try {
      await api.delete(`/client-portal/tokens/${id}`);
      setTokens(prev => prev.filter(t => t.id !== id));
      toast.success('Eliminado');
    } catch (e) { toast.error('Error'); }
  };

  const copyLink = (token) => {
    const link = `${BASE_URL}/portal/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado al portapapeles');
  };

  const permLabels = {
    view_progress: 'Progreso', view_photos: 'Fotos', view_milestones: 'Hitos',
    view_documents: 'Documentos', view_checklists: 'Checklists',
  };

  return (
    <div className="client-portal-page">
      <div className="page-header">
        <div><h1><Link2 size={24} /> Portal de Cliente</h1><p className="subtitle">Gestión de accesos externos a proyectos</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Crear Acceso</button>
      </div>

      <div className="portal-tokens-list">
        {tokens.length === 0 && !loading && (
          <div className="empty-state"><Link2 size={48} /><h3>Sin accesos de cliente</h3><p>Crea un link de acceso para que tus clientes vean el progreso</p></div>
        )}
        {tokens.map(t => (
          <div key={t.id} className={`portal-token-card ${!t.is_active ? 'inactive' : ''}`}>
            <div className="pt-card-header">
              <div className="pt-card-info">
                <span className="pt-project">{t.project_code} — {t.project_name}</span>
                <span className="pt-email">{t.email}</span>
              </div>
              <div className="pt-card-actions">
                <button className="btn-icon" title="Copiar link" onClick={() => copyLink(t.token)}><Copy size={16} /></button>
                <a className="btn-icon" href={`/portal/${t.token}`} target="_blank" rel="noopener noreferrer" title="Abrir portal"><ExternalLink size={16} /></a>
                <button className="btn-icon" onClick={() => toggleToken(t.id, t.is_active)}>
                  {t.is_active ? <ToggleRight size={18} style={{ color: '#10B981' }} /> : <ToggleLeft size={18} style={{ color: '#64748B' }} />}
                </button>
                <button className="btn-icon" onClick={() => deleteToken(t.id)}><Trash2 size={16} style={{ color: '#EF4444' }} /></button>
              </div>
            </div>
            <div className="pt-card-perms">
              {Object.entries(t.permissions || {}).map(([k, v]) => (
                <span key={k} className={`pt-perm ${v ? 'active' : ''}`}>
                  {v ? <Eye size={10} /> : <EyeOff size={10} />} {permLabels[k] || k}
                </span>
              ))}
            </div>
            <div className="pt-card-meta">
              <span>Accesos: {t.access_count || 0}</span>
              {t.last_accessed_at && <span>Último: {new Date(t.last_accessed_at).toLocaleString('es')}</span>}
              {t.expires_at && <span>Expira: {new Date(t.expires_at).toLocaleDateString('es')}</span>}
              <span className={`pt-status ${t.is_active ? '' : 'disabled'}`}>{t.is_active ? '● Activo' : '○ Desactivado'}</span>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Crear Acceso de Cliente</h3><button className="btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-group">
                <label>Proyecto *</label>
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Email del cliente *</label>
                <input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="cliente@empresa.com" />
              </div>
              <div className="form-group">
                <label>Expira en (días, vacío = sin expiración)</label>
                <input type="number" value={createForm.expiresInDays} onChange={e => setCreateForm(f => ({ ...f, expiresInDays: parseInt(e.target.value) || '' }))} />
              </div>
              <div className="form-group">
                <label>Permisos</label>
                <div className="perm-toggles">
                  {Object.entries(permLabels).map(([k, v]) => (
                    <label key={k} className="perm-toggle">
                      <input type="checkbox" checked={!!createForm.permissions[k]}
                        onChange={e => setCreateForm(f => ({ ...f, permissions: { ...f.permissions, [k]: e.target.checked } }))} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={createToken}>Crear Acceso</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
